import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureError } from '../../lib/monitoring';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/** Last line of defence: a readable message with a reload button instead of a blank window. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('MailPilot crashed', error, info.componentStack);
    captureError(error, { where: 'error-boundary' });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <AppProblem title="Something went wrong" message={this.state.error.message} />;
  }
}

/** Full-screen problem notice used by the error boundary and the server-version check. */
export function AppProblem({ title, message, hint }: { title: string; message: string; hint?: string }) {
  return (
    <div className="flex min-h-screen min-h-[100dvh] items-center justify-center bg-bg p-6 text-ink">
      <div className="w-full max-w-md rounded-3xl bg-surface p-7 text-center shadow-card">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-warn-soft text-warn-ink">
          <AlertTriangle className="h-6 w-6" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">{title}</h1>
        <p className="mt-2 text-[0.8125rem] text-muted">{message}</p>
        {hint && <p className="mt-3 rounded-xl bg-raised px-3 py-2 text-xs text-muted">{hint}</p>}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-[0.875rem] font-medium text-primary-fg hover:bg-primary-hover"
        >
          <RotateCcw className="h-4 w-4" /> Reload
        </button>
      </div>
    </div>
  );
}
